import { useParams } from "@tanstack/react-router";
import { Building2, FolderKanban, Mail, Phone, User } from "lucide-react";
import { useState } from "react";
import { ComplianceDocumentsTable } from "@/components/compliance/ComplianceDocumentsTable";
import { EditComplianceDialog } from "@/components/compliance/EditComplianceDialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCompany } from "@/hooks/use-companies";
import type { ComplianceDocument } from "@/hooks/use-compliance";
import { useComplianceDocuments } from "@/hooks/use-compliance";
import { useContacts } from "@/hooks/use-contacts";
import { useProjects } from "@/hooks/use-projects";

function CompanyOverview({
  company,
}: {
  company: {
    companyName: string;
    trade: string | null;
    contactPerson: string | null;
    email: string | null;
    phone: string | null;
    type: string | null;
    status: string | null;
  };
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Company Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Name</dt>
            <dd className="mt-1 text-sm">{company.companyName}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Type</dt>
            <dd className="mt-1 text-sm">
              <Badge variant="outline">{company.type ?? "N/A"}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Trade</dt>
            <dd className="mt-1 text-sm">{company.trade ?? "N/A"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Status</dt>
            <dd className="mt-1 text-sm">{company.status ?? "N/A"}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Contact Person</dt>
            <dd className="mt-1 text-sm flex items-center gap-1">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              {company.contactPerson ?? "N/A"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Email</dt>
            <dd className="mt-1 text-sm flex items-center gap-1">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              {company.email ?? "N/A"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Phone</dt>
            <dd className="mt-1 text-sm flex items-center gap-1">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              {company.phone ?? "N/A"}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function ComplianceTab({ companyId }: { companyId: string }) {
  const { data, isLoading, isError, error, refetch } = useComplianceDocuments(companyId);
  const [editDoc, setEditDoc] = useState<ComplianceDocument | null>(null);

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load compliance documents"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <ComplianceDocumentsTable
        documents={data ?? []}
        hideCompanyColumn
        onEdit={(doc) => setEditDoc(doc)}
        csvFilename={`compliance-${companyId}`}
      />
      <EditComplianceDialog doc={editDoc} onClose={() => setEditDoc(null)} />
    </>
  );
}

function ContactsTab({ companyId }: { companyId: string }) {
  const { data, isLoading, isError, error, refetch } = useContacts();

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load contacts"} onRetry={() => refetch()} />
    );
  }

  const items = (data ?? []).filter((c) => c.companyId === companyId);

  if (items.length === 0) {
    return (
      <EmptyState
        icon={User}
        title="No contacts"
        description="No contacts linked to this company."
      />
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Type</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((contact) => (
            <TableRow key={contact.id}>
              <TableCell className="font-medium">
                {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "--"}
              </TableCell>
              <TableCell>{contact.title ?? "--"}</TableCell>
              <TableCell>{contact.email ?? "--"}</TableCell>
              <TableCell>{contact.phone ?? "--"}</TableCell>
              <TableCell>
                <Badge variant="outline">{contact.type ?? "--"}</Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ProjectsTab({ companyId }: { companyId: string }) {
  const { data, isLoading, isError, error, refetch } = useProjects();

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load projects"} onRetry={() => refetch()} />
    );
  }

  const items = (data ?? []).filter((p) => p.client === companyId);

  if (items.length === 0) {
    return (
      <EmptyState
        icon={FolderKanban}
        title="No projects"
        description="No projects assigned to this company."
      />
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Location</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((project) => (
            <TableRow key={project.id}>
              <TableCell className="font-medium">{project.name}</TableCell>
              <TableCell>
                <Badge variant="outline">{project.status}</Badge>
              </TableCell>
              <TableCell>{project.type}</TableCell>
              <TableCell>{project.location ?? "--"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export function CompanyDetailPage() {
  const { id } = useParams({ strict: false });
  const companyId = id ?? "";
  const { data: company, isLoading, isError, error, refetch } = useCompany(companyId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load company"} onRetry={() => refetch()} />
    );
  }

  if (!company) {
    return (
      <EmptyState
        icon={Building2}
        title="Company not found"
        description="The requested company could not be found."
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={company.companyName}
        description={`${company.type ?? "Company"} details`}
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <CompanyOverview company={company} />
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <ComplianceTab companyId={companyId} />
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <ContactsTab companyId={companyId} />
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <ProjectsTab companyId={companyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
